import React, { useState } from 'react';

import { FormStates, MonthValues } from './constants';
import { CreateForm } from './create-form';
import { CreatedSummary } from './created-summary';
import { useExpirationDate } from './hooks';
import { LastCall } from './last-call';
import { modalCancelButtonMessages, modalTitleMessages, modalActionButtonMessages } from './messages';
import { SimpleModal } from '../../../../../components/simple-modal';

const DefaultSelectedExpire = MonthValues.Twelve;

/**
 * This component renders a modal to create a new API Key V1
 * @param {object} props
 * @param {boolean} props.isLoading - Boolean to set the modal state to loading
 * @param {(expiration: string) => Promise<import('../../types/api-keys').APIKeysV1>} props.generateV1ApiKeys - Handler to create a new V1 API key
 * @param {import('../../../../../components/simple-modal/simple-modal').ModalCloseHandler} props.onModalClose - Callback when the user clicks another expiration time
 * @returns {JSX.Element} The rendered component.
 */
export const GenerateV1KeyModal = ({ onModalClose, generateV1ApiKeys, isLoading }) => {
  const { getDatePlusNumberOfMonths } = useExpirationDate();

  /** @type {ReturnType<typeof useState<import('../../types/api-keys').APIKeysV1>>} */
  const [createdApiKey, setCreatedApiKey] = useState();

  const [currentState, setCurrentState] = useState(FormStates.Form);
  const [selectedExpirationTime, setSelectedExpirationTime] = useState(DefaultSelectedExpire);

  const renderModalCancelButton = [FormStates.Form, FormStates.LastCall].includes(currentState);

  const modalActionHandler = () => {
    const expirationDate = getDatePlusNumberOfMonths(selectedExpirationTime);

    const generate = () =>
      generateV1ApiKeys(expirationDate.toISO()).then((apiKey) => {
        /** If there is an error the apiKey will be undefined */
        if (apiKey) {
          setCreatedApiKey(apiKey);
          setCurrentState(FormStates.Summary);
        }
      });

    switch (currentState) {
      case FormStates.Form:
        generate();
        break;

      case FormStates.Summary:
        setCurrentState(FormStates.LastCall);
        break;

      /** default is the FormStates.LastCall state */
      default:
        setCurrentState(FormStates.Summary);
        break;
    }
  };

  const renderModalChildren = () => {
    switch (currentState) {
      case FormStates.Form:
        return <CreateForm onSelected={setSelectedExpirationTime} selected={selectedExpirationTime} />;

      case FormStates.Summary:
        // @ts-expect-error ???
        return <CreatedSummary createdApiKey={createdApiKey} />;

      default:
        return <LastCall />;
    }
  };

  return (
    <SimpleModal
      actionMessage={modalActionButtonMessages[currentState]}
      cancelMessage={modalCancelButtonMessages[currentState]}
      headerMessage={modalTitleMessages[currentState]}
      isActionDisabled={isLoading}
      onAction={modalActionHandler}
      onCancel={renderModalCancelButton ? onModalClose : undefined}
      onClose={onModalClose}
      onModalClose={onModalClose}
      size='medium'
    >
      {renderModalChildren()}
    </SimpleModal>
  );
};
